import {
  ComponentRendering,
  Text,
  TextField,
  Field,
  withDatasourceCheck,
  LinkField,
  Item,
  getFieldValue,
  useSitecoreContext,
  LayoutServicePageState,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';

import { useRouter } from 'next/router';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import { ButtonVariant } from 'components/helpers/Button/Button';
import { IconVariant } from 'components/helpers/Icon';
import Link, { LinkVariant } from 'components/helpers/Link/Link';
import ResponsiveImages from 'components/helpers/ResponsiveImages/ResponsiveImages';
import { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { getRenderingParam } from 'lib/rendering-params';
import { BREAKPOINTS } from 'models/breakpoints';
import { RecordOfFields } from 'models/fields';

import styles from './ImageTextCallout.module.scss';
import { ResponsiveImageFields } from 'models/responsive-image';

type ImageTextCalloutProps = ComponentProps & {
  fields: ResponsiveImageFields & {
    content: Field<string>;
    horizontalBorderMode: Field<string>;
    imageDirection: Item;
    link: LinkField;
    linkIcon: Item | null;
    linkMode: Item | null;
    presentationMode: Item;
    title: Field<string>;
    titleHeadingLevel: Item;
  };
  rendering: ComponentRendering;
};

const cx = classNames.bind(styles);

const ImageTextCallout = (props: ImageTextCalloutProps): JSX.Element | null => {
  const { className, fields, params } = props;

  const [isMobile, setIsMobile] = React.useState(false);
  const { sitecoreContext } = useSitecoreContext();

  const horizontalBorderModeText = getRenderingParam(params.horizontalBorderMode);

  const imageDirectionItemFields = fields.imageDirection.fields as RecordOfFields;
  const imageDirection = getFieldValue<string>(imageDirectionItemFields, 'Value') || 'left';

  const titleHeadingLevelItemFields = fields.titleHeadingLevel?.fields as RecordOfFields;
  const titleHeadingLevel = getFieldValue<string>(titleHeadingLevelItemFields, 'Value') || 'h2';

  const linkIconField = fields.linkIcon?.fields as RecordOfFields;
  const linkIconValue = getFieldValue<IconVariant>(linkIconField, 'Value');

  const linkModeField = fields.linkMode?.fields as RecordOfFields;
  const linkModeValue = getFieldValue<ButtonVariant | LinkVariant>(linkModeField, 'Value');

  const presentationModeItemFields = fields.presentationMode?.fields as RecordOfFields;
  const presentationMode = getFieldValue<string>(presentationModeItemFields, 'Value') || '50-50';

  const interactiveElementForLink =
    sitecoreContext.pageEditing || fields.link?.value?.href ? (
      <Link field={fields.link} icon={linkIconValue} variant={linkModeValue} />
    ) : null;

  type OnlyDisplayFor = {
    queryStringValue: TextField;
    queryString: TextField;
    includeNotSpecified: IncludeNotSpecified;
  };

  type IncludeNotSpecified = {
    value: boolean;
  };

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.MOBILE_MAX}px)`);
    mql.addEventListener('change', (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    });
    setIsMobile(window.innerWidth <= BREAKPOINTS.MOBILE_MAX);
  }, []);

  const router = useRouter();

  let shouldDisplay = true;
  let checkQueryParam = '';
  let checkQueryParamValue = '';

  if (sitecoreContext.pageState === LayoutServicePageState.Normal && props.rendering?.params?.onlyDisplayFor) {
    const personalizationParam = JSON.parse(props.rendering?.params?.onlyDisplayFor || '') as OnlyDisplayFor;
    checkQueryParam = personalizationParam.queryString.value?.toString().toLowerCase() || '';
    checkQueryParamValue = personalizationParam.queryStringValue.value?.toString().toLowerCase() || '';
    const includeNotSpecified = personalizationParam.includeNotSpecified.value;

    if (checkQueryParam && checkQueryParamValue) {
      shouldDisplay = false;

      if (router.isReady) {
        if (router.asPath.indexOf('?') > 0) {
          let matchingKeyValue = undefined;
          let queryParamValue = undefined;

          // make our query parameter case insensitive, if we find a passed in param with different case, switch to use that.
          for (const key in router.query) {
            if (key.toLowerCase() === checkQueryParam) {
              matchingKeyValue = router.query[key];
              queryParamValue = (
                Array.isArray(matchingKeyValue) ? matchingKeyValue[0] : matchingKeyValue || ''
              ).toLowerCase();
              break;
            }
          }

          // if match, or param not present and include not specified, then show.
          if (queryParamValue === checkQueryParamValue || (!queryParamValue && includeNotSpecified)) {
            shouldDisplay = true;
          }
        } else {
          // if no query string period, show if includeNotSpecified is true
          shouldDisplay = includeNotSpecified;
        }
      }
    }
  }

  const textColumn = (
    <div key="text-column" className={cx('text-column')}>
      <Text tag={titleHeadingLevel} field={fields.title} />
      <RichTextWrapper className={cx('text')} field={fields.content} />
      {interactiveElementForLink}
    </div>
  );

  const imageColumn = (
    <div key="text-image" className={cx('image-column')}>
      <ResponsiveImages
        desktopImage={props.fields.desktopImage}
        mobileImage={props.fields.mobileImage}
        tabletImage={props.fields.tabletImage}
        imageType={fields.imageType}
        imageAreaBackgroundColor={fields.imageAreaBackgroundColor}
      />
    </div>
  );

  return shouldDisplay ? (
    <section
      className={cx(
        {
          'section-horizontal-bottom': horizontalBorderModeText === 'bottom' || horizontalBorderModeText === 'both',
          'section-horizontal-top': horizontalBorderModeText === 'top' || horizontalBorderModeText === 'both',
        },
        'image-text-callout',
        `callout-${presentationMode}`,
        className
      )}
    >
      {imageDirection === 'left' || isMobile ? [imageColumn, textColumn] : [textColumn, imageColumn]}
    </section>
  ) : null;
};

export default compose<ImageTextCalloutProps>(withDatasourceCheck(), withStyles())(ImageTextCallout);
